import { isAxiosError } from 'axios'

/** One entry of `error.details` (SPEC-001 §2.2). */
export interface ErrorDetail {
  field?: string
  code?: string
  message?: string
  [key: string]: unknown
}

/**
 * The API's error envelope as an exception. Branch on `code`, never on
 * `message`; show `message` to the user.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details: ErrorDetail[] = [],
    public readonly requestId: string | null = null,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Validation messages keyed by field path, for showing under form inputs. */
  get fieldErrors(): Record<string, string> {
    const errors: Record<string, string> = {}
    for (const detail of this.details) {
      if (detail.field && detail.message && !errors[detail.field]) errors[detail.field] = detail.message
    }
    return errors
  }

  static from(error: unknown): ApiError {
    if (error instanceof ApiError) return error

    if (isAxiosError(error) && error.response) {
      const body = error.response.data as
        | { error?: { code?: string; message?: string; details?: ErrorDetail[]; request_id?: string } }
        | undefined
      const envelope = body?.error
      if (envelope?.code) {
        return new ApiError(
          error.response.status,
          envelope.code,
          envelope.message ?? 'Something went wrong.',
          envelope.details ?? [],
          envelope.request_id ?? null,
        )
      }
      return new ApiError(error.response.status, 'UNKNOWN', 'Something went wrong. Please try again.')
    }

    return new ApiError(
      0,
      'NETWORK_ERROR',
      'Could not reach the server. Check your connection and try again.',
    )
  }
}
